/**
 * Os exames do paciente e as etapas de cada pedido (#811, clínica).
 *
 * Só nas organizações com a funcionalidade "clinica" ligada; sem ela, as rotas
 * respondem 404. Cada pedido tem as suas etapas, na ordem dele, e o ok segue
 * essa ordem: guarda quando e quem deu.
 */

import { z } from 'zod';
import { MudancaDeCamposSchema, ValoresDeCamposSchema } from './campos-personalizados.js';

export const EtapaDoExameSchema = z.object({
  id: z.string().uuid(),
  /** A posição no pedido, a partir de 1. */
  ordem: z.number().int().min(1),
  nome: z.string(),
  /** A etapa da clínica de onde veio, ou `null` se foi escrita só para este pedido. */
  etapa_da_clinica_id: z.string().uuid().nullable(),
  /** Quando recebeu o ok, ou `null` enquanto aberta. */
  concluida_em: z.string().datetime({ offset: true }).nullable(),
  concluida_por: z.object({ id: z.string().uuid(), nome: z.string().nullable() }).nullable(),
});
export type EtapaDoExame = z.infer<typeof EtapaDoExameSchema>;

export const ExameDoPacienteSchema = z.object({
  id: z.string().uuid(),
  exame: z.string(),
  /** Os códigos que vêm da agenda da clínica. */
  codigo_do_exame: z.string().nullable(),
  codigo_do_pedido: z.string().nullable(),
  laboratorio: z.string().nullable(),
  solicitante: z.string().nullable(),
  urgente: z.boolean(),
  observacao: z.string().nullable(),
  criado_em: z.string().datetime({ offset: true }),
  cancelado_em: z.string().datetime({ offset: true }).nullable(),
  versao: z.number().int().min(1),
  /** Na ordem do pedido. */
  etapas: z.array(EtapaDoExameSchema),
  /** Os campos personalizados do pedido (#568), pelo id do campo. Dado de saúde. */
  campos: ValoresDeCamposSchema,
});
export type ExameDoPaciente = z.infer<typeof ExameDoPacienteSchema>;

export const ExamesDoPacienteSchema = z.object({
  /** O mais novo primeiro. */
  itens: z.array(ExameDoPacienteSchema),
});
export type ExamesDoPaciente = z.infer<typeof ExamesDoPacienteSchema>;

const CamposDoExame = {
  exame: z.string().max(200),
  codigo_do_exame: z.string().max(60).nullable(),
  codigo_do_pedido: z.string().max(60).nullable(),
  laboratorio: z.string().max(120).nullable(),
  solicitante: z.string().max(120).nullable(),
  urgente: z.boolean(),
  observacao: z.string().max(2000).nullable(),
  /** Só os campos personalizados que mudaram (#568); `null` esvazia. */
  campos: MudancaDeCamposSchema.optional(),
};

export const EtapaNovaDoExameSchema = z
  .object({
    nome: z.string().max(80),
    /** A etapa da clínica, ou `null` para uma escrita só neste pedido. */
    etapa_da_clinica_id: z.string().uuid().nullable(),
  })
  .strict();
export type EtapaNovaDoExame = z.infer<typeof EtapaNovaDoExameSchema>;

export const CriarExameDoPacienteSchema = z
  .object({
    ...CamposDoExame,
    /** Na ordem em que vão acontecer. */
    etapas: z.array(EtapaNovaDoExameSchema).max(20),
  })
  .strict();
export type CriarExameDoPaciente = z.infer<typeof CriarExameDoPacienteSchema>;

export const EditarExameDoPacienteSchema = z
  .object({ ...CamposDoExame, versao: z.number().int().min(1) })
  .strict();
export type EditarExameDoPaciente = z.infer<typeof EditarExameDoPacienteSchema>;

export const DefinirEtapasDoExameSchema = z
  .object({
    /** As etapas ainda abertas, na ordem nova. As que já têm ok ficam como estão. */
    etapas: z.array(EtapaNovaDoExameSchema).max(20),
    versao: z.number().int().min(1),
  })
  .strict();
export type DefinirEtapasDoExame = z.infer<typeof DefinirEtapasDoExameSchema>;

export const EtapaDaClinicaSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  ordem: z.number().int().min(0),
  ativa: z.boolean(),
});
export type EtapaDaClinica = z.infer<typeof EtapaDaClinicaSchema>;

export const EtapasDaClinicaSchema = z.object({
  /** Na ordem sugerida para um pedido novo. */
  itens: z.array(EtapaDaClinicaSchema),
});
export type EtapasDaClinica = z.infer<typeof EtapasDaClinicaSchema>;

export const CriarEtapaDaClinicaSchema = z.object({ nome: z.string().max(80) }).strict();
export type CriarEtapaDaClinica = z.infer<typeof CriarEtapaDaClinicaSchema>;

export const EditarEtapaDaClinicaSchema = z
  .object({ nome: z.string().max(80), ativa: z.boolean() })
  .strict();
export type EditarEtapaDaClinica = z.infer<typeof EditarEtapaDaClinicaSchema>;

export const OrdenarEtapasDaClinicaSchema = z
  .object({ ids: z.array(z.string().uuid()).max(200) })
  .strict();
export type OrdenarEtapasDaClinica = z.infer<typeof OrdenarEtapasDaClinicaSchema>;
